import React, { useEffect, useState } from 'react';
import { adminApi } from '../../services/adminApi';
import { AuditLog } from '../../types';
import { ScrollText, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 50;

const ACTION_LABELS: Record<string, string> = {
  create_organization: 'Created workspace',
  disable_organization: 'Disabled workspace',
  enable_organization: 'Enabled workspace',
  impersonate_organization: 'Opened as owner',
  create_invite: 'Created invite',
  revoke_invite: 'Revoked invite',
  create_admin: 'Added platform admin',
  deactivate_admin: 'Deactivated admin',
  reactivate_admin: 'Reactivated admin',
};

const RESOURCE_COLORS: Record<string, string> = {
  organization: 'bg-blue-50 text-blue-700 border-blue-200',
  invite: 'bg-amber-50 text-amber-700 border-amber-200',
  admin: 'bg-purple-50 text-purple-700 border-purple-200',
};

function formatMetadata(raw: string | null): string {
  if (!raw) return '';
  try {
    const obj = JSON.parse(raw);
    return Object.entries(obj)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
      .join(' · ');
  } catch {
    return raw;
  }
}

export function AdminPortalAuditLog() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    load(0);
  }, [filterType]);

  const load = async (pageNum: number) => {
    setLoading(true);
    setError('');
    try {
      const { logs: rows } = await adminApi.listAuditLogs({
        limit: PAGE_SIZE + 1,
        offset: pageNum * PAGE_SIZE,
        resource_type: filterType || undefined,
      });
      setHasMore(rows.length > PAGE_SIZE);
      setLogs(rows.slice(0, PAGE_SIZE));
      setPage(pageNum);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto pb-24 p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Audit Log</h1>
          <p className="text-slate-500 font-medium mt-1">Immutable record of all platform admin actions.</p>
        </div>
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="h-11 px-4 bg-white border border-slate-200 rounded-2xl text-[14px] font-semibold text-slate-700 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-all"
        >
          <option value="">All types</option>
          <option value="organization">Organizations</option>
          <option value="invite">Invites</option>
          <option value="admin">Admins</option>
        </select>
      </div>

      {error && (
        <div className="px-5 py-4 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-6 font-medium">
          {error}
        </div>
      )}

      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200/60 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64 text-slate-400 font-medium">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3 text-slate-400">
            <ScrollText className="w-8 h-8" />
            <span className="font-medium">No audit events yet.</span>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {logs.map((log) => {
              const meta = formatMetadata(log.metadata);
              return (
                <div key={log.id} className="px-6 py-4 hover:bg-slate-50/40 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-28 pt-0.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border ${RESOURCE_COLORS[log.resource_type] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                        {log.resource_type}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-bold text-slate-800 text-[14px]">
                          {ACTION_LABELS[log.action] ?? log.action.replace(/_/g, ' ')}
                        </span>
                        {log.resource_id && (
                          <span className="text-[12px] font-mono text-slate-400 truncate">{log.resource_id}</span>
                        )}
                      </div>
                      {meta && (
                        <div className="text-[12px] text-slate-500 mt-0.5 leading-relaxed">{meta}</div>
                      )}
                      <div className="text-[12px] text-slate-400 mt-1">
                        by <span className="font-medium text-slate-600">{log.admin_email}</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-[12px] text-slate-400 whitespace-nowrap pt-0.5">
                      {new Date(log.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {(page > 0 || hasMore) && !loading && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/30">
            <button
              onClick={() => load(page - 1)}
              disabled={page === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-bold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <span className="text-[13px] font-medium text-slate-500">Page {page + 1}</span>
            <button
              onClick={() => load(page + 1)}
              disabled={!hasMore}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-bold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
